import type { ReactNode, TextareaHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import { twJoin } from 'tailwind-merge';

import { twm } from '../../../core/themes/tw-merge';
import { InfoPopover } from '../InfoPopover';
import { Label } from '../typography';

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  errorMessage?: string;
  children?: ReactNode;
  info?: string;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, label, id, errorMessage, children, info, ...rest }, ref) => {
    return (
      <div>
        {label ? (
          <div className="flex items-center gap-2 mb-1.5">
            <Label htmlFor={id} className={twJoin(!!errorMessage && 'text-text-error', 'mb-0')}>
              {label}
            </Label>
            {info ? <InfoPopover text={info} /> : null}
          </div>
        ) : null}
        <div className="relative">
          <textarea
            {...rest}
            ref={ref}
            id={id}
            rows={rest.rows ?? 4}
            className={twm(
              'bg-surface-secondary w-full rounded-md px-3 py-4 ',
              !!errorMessage &&
                'placeholder-fg-error-primary border-2 border-border-error text-text-error ring-border-error outline-border-error',
              className,
            )}
          />
          {children}
        </div>
        {errorMessage ? <p className="mt-1 text-text-error text-body-sm leading-lh-body-sm">{errorMessage}</p> : null}
      </div>
    );
  },
);

TextArea.displayName = 'TextArea';
